import { useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import {
  getSearchOptions,
  getEntityTypeFilter,
} from "redux/SingleDataset/selectors";
import DropDown from "views/pages/DataPage/CardsView/DataPageFilters/DropDown";

import {
  EntityType,
  FilterKey,
  FilterOption,
  isValidEntityType,
  MetadataItem,
} from "types";
import ChevronIcon from "assets/icons/ChevronIcon";
import {
  fetchDatasetContextualLabels,
  fetchMetadataSources,
  setCustomMetadata,
} from "redux/SingleDataset/actions";
import { fetchDataset } from "redux/SingleDataset/actions";
import { amplitudeTrack } from "helpers/utility/amplitude";
import { useLocation, useParams, useNavigate } from "react-router";
import { AMP_DATASET_EXPLORATION_EVENT__ENTITY_TYPE_CHANGED } from "helpers/constants/amplitudeEvents";
import { AMP_DATASET_EXPLORATION_PROPERTY__ENTITY_TYPE } from "helpers/constants/amplitudeProperties";
import {
  LABELS_PARAMETER,
  DUPLICATE_THRESHOLD,
  ISSUE_TYPE_PARAMETER,
  ENTITY_TYPE_PARAMETER,
  SEARCH_TEXT_PARAMETER,
  METADATA_SOURCES_PARAMETER,
  SEARCH_TEXT_THRESHOLD_PARAMETER,
  VQL_PARAMETER,
} from "helpers/constants/miscellaneous";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";
import styles from "./style.module.scss";
import TooltipWrapper from "views/uikit/TooltipWrapper";
import { formatCapsString } from "helpers/utility/utilities";
import {getFilterKeyByFilterType, removeFiltersFromVqlQuery} from "../../../../../../helpers/utility/vqlHelpers";
import {FILTER_MENU_OPTIONS} from "../../../../../../helpers/utility/queryFilterHelpers";
interface EntityButtonProps {
  hide?: boolean;
}

const EntityButton = (props: EntityButtonProps) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();
  const queryParamsFilters = useAllQueryParamsFilters();
  const { hide } = props;
  const entityTypeFromQueryParam = isValidEntityType(
    queryParamsFilters[ENTITY_TYPE_PARAMETER]
  )
    ? queryParamsFilters[ENTITY_TYPE_PARAMETER]
    : null;
  const entityTypeFilter = useSelector((state) =>
    getEntityTypeFilter(state, entityTypeFromQueryParam)
  );

  const imageSearchEnabled =
    useSelector((state) => getSearchOptions(state, "IMAGES"))?.enabled || false;

  const objectSearchEnabled =
    useSelector((state) => getSearchOptions(state, "OBJECTS"))?.enabled ||
    false;

  const { datasetId } = useParams();
  const location = useLocation();

  const dispatch = useDispatch();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const filterRef = useRef<HTMLDivElement>(null);

  const selectedEntityTypes = entityTypeFilter.filter(
    (option: FilterOption) => option.selected
  );

  const entityDropdownClassnames = classNames(styles.entityDropdown, {
    [styles.disabledEntityDropdown]: hide,
  });

  if (!selectedEntityTypes.length) {
    selectedEntityTypes.push(
      entityTypeFilter.find((entity) => entity.text.toLowerCase() === "images")!
    );
  }

  const dropdownSelectedEntityTypes = selectedEntityTypes.map(
    (option: FilterOption) => option.id
  );

  const selectedTitle = formatCapsString(selectedEntityTypes[0].text);
  const content = showDropdown
    ? ""
    : `Apply this filter to display only the data entries from a specific type: Images or Objects within images`;
  const dropdownItems = entityTypeFilter.map((entity: MetadataItem) => ({
    id: entity.id,
    text: formatCapsString(entity.text),
  }));

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const resetFilters = (entityType: EntityType) => {
        const filterKeysToRemove = FILTER_MENU_OPTIONS
      .filter((filter) => !filter.entityTypes.includes(entityType))
      .map((filter) => getFilterKeyByFilterType(filter.type))
      .filter((key): key is FilterKey => !!key);

    if (filterKeysToRemove.length && searchParams.has(VQL_PARAMETER)) {
      removeFiltersFromVqlQuery(searchParams, filterKeysToRemove);
    }
  }

  const handleFilterSelection = async (value: number) => {
    const selectedEntityType = entityTypeFilter.find(
      (option: FilterOption) => option.id === value
    );
    if (!selectedEntityType) {
      setShowDropdown(false);
      return;
    }

    if (selectedEntityType.selected) {
      setShowDropdown(false);
      return;
    }

    setShowDropdown(false);
    resetFilters(selectedEntityType.text.toUpperCase() as EntityType);

    if (searchParams.has(LABELS_PARAMETER)) {
      //remove labels on entity type change
      searchParams.delete(LABELS_PARAMETER);
    }

    if (searchParams.has(SEARCH_TEXT_PARAMETER)) {
      const isImage = selectedEntityType.text.toUpperCase() === "IMAGES";
      const isSearchEnabled = isImage
        ? imageSearchEnabled
        : objectSearchEnabled;

      if (!isSearchEnabled) {
        //remove search query if search is not enabled for the selected entity type
        searchParams.delete(SEARCH_TEXT_PARAMETER);
        searchParams.delete(SEARCH_TEXT_THRESHOLD_PARAMETER);
      }
    }

    amplitudeTrack(AMP_DATASET_EXPLORATION_EVENT__ENTITY_TYPE_CHANGED, {
      [AMP_DATASET_EXPLORATION_PROPERTY__ENTITY_TYPE]: selectedEntityType.text,
    });

    //reset duplicates threshold
    if (searchParams.has(DUPLICATE_THRESHOLD)) {
      searchParams.delete(DUPLICATE_THRESHOLD);
      searchParams.delete(ISSUE_TYPE_PARAMETER);
    }
    searchParams.delete(METADATA_SOURCES_PARAMETER);
    searchParams.set(ENTITY_TYPE_PARAMETER, selectedEntityType.text);
    navigate({ search: `?${searchParams.toString()}` });
    dispatch(
      fetchDatasetContextualLabels(datasetId, {
        [ENTITY_TYPE_PARAMETER]: selectedEntityType.text,
      }) as any
    );
    dispatch(fetchDataset(datasetId) as any);
    dispatch(
      fetchMetadataSources(
        datasetId!,
        selectedEntityType.text.toUpperCase() as EntityType
      ) as any
    );
    if (selectedEntityType.text.toUpperCase() === "OBJECTS") {
      //reset custom metadata
      dispatch(setCustomMetadata(null));
    }
  };

  const handleOutsideClick = (event: any) => {
    if (filterRef.current && !filterRef.current.contains(event.target)) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    if (showDropdown) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
    //eslint-disable-next-line
  }, [showDropdown]);

  return (
    <div
      className={entityDropdownClassnames}
      ref={filterRef}
      data-tooltip-content={content}
      data-tooltip-id="entity-filter"
    >
      {!showDropdown && <TooltipWrapper id="entity-filter" />}
      <div
        className={styles.header}
        onClick={toggleDropdown}
        data-testid="entity-filter-button"
      >
        <div className={styles.selectedTitle}>{selectedTitle}</div>
        <div className={styles.toggleIcon}>
          {showDropdown ? (
            <ChevronIcon direction="up" />
          ) : (
            <ChevronIcon direction="down" />
          )}
        </div>
      </div>
      {showDropdown && (
        <div className={styles.childWrapper}>
          <DropDown
            variant="light"
            showRadioButton={false}
            classname={styles.dropdown}
            items={dropdownItems}
            selectedItemsIds={dropdownSelectedEntityTypes}
            onItemSelection={handleFilterSelection}
          />
        </div>
      )}
    </div>
  );
};

export default EntityButton;
